import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useBranchesQuery } from "@/modules/branches/presentation/api/queries";
import type {
  OverrideMatrixRow,
  VariantMatrixRow,
} from "@/modules/pricing/domain/types";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";


import { useNavigate } from "@tanstack/react-router";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RotateCcwIcon,
  SearchIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useDeactivateOverrideMutation,
  usePricingMatrixQuery,
  useUpsertOverrideMutation,
} from "../api/queries";
import { PricingToolbar } from "../ui/PricingToolbar";

function formatLak(n: number): string {
  return n.toLocaleString("en-US");
}

function getEffectivePrice(
  variantId: string,
  basePriceLak: number,
  branchId: string,
  overrides: OverrideMatrixRow[],
  pending: number | null,
): number {
  if (pending !== null) return pending;
  const o = overrides.find(
    (x) => x.variantId === variantId && x.branchId === branchId && x.isActive,
  );
  return o ? o.overridePriceLak : basePriceLak;
}

function getOverride(
  variantId: string,
  branchId: string,
  overrides: OverrideMatrixRow[],
): OverrideMatrixRow | undefined {
  return overrides.find(
    (x) => x.variantId === variantId && x.branchId === branchId && x.isActive,
  );
}

type PendingOverride = { value: number } | "clear";

const PAGE_SIZE = 10;

export function BranchPricingPage() {
  const nav = useNavigate({ from: "/app/pricing/branch-overrides" });
  const [branchId, setBranchId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string>("all");
  const [searchInputValue] = useState("");
  const [searchProducts, setSearchProducts] = useState("");
  const [page, setPage] = useState(1);

  const debouncedSetSearch = useDebounceCallback((val: string) => {
    setSearchProducts(val);
    setPage(1);
  }, 400);
  const [pending, setPending] = useState<Record<string, PendingOverride>>({});
  const [saving, setSaving] = useState(false);

  const canUpdate = useActionPermission(["pricing:update"]);

  const branchesQuery = useBranchesQuery({ limit: 200, offset: 0 });
  const matrixQuery = usePricingMatrixQuery({
    branchIds: branchId ? [branchId] : [],
    productId: productId === "all" ? undefined : productId,
    search: searchProducts.trim() || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const upsertMutation = useUpsertOverrideMutation();
  const deactivateMutation = useDeactivateOverrideMutation();

  const branches = branchesQuery.data?.data ?? [];
  const variants = matrixQuery.data?.variants ?? [];
  const overrides = matrixQuery.data?.overrides ?? [];
  const totalVariants = matrixQuery.data?.totalVariants ?? 0;

  const selectedBranchId = branchId;

  const totalPages = Math.max(1, Math.ceil(totalVariants / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const fromItem = totalVariants === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const toItem = Math.min(currentPage * PAGE_SIZE, totalVariants);

  const branchOptions = useMemo(
    () => [
      { value: "none", label: "ເລືອກສາຂາ..." },
      ...branches.map((b) => ({ value: b.id, label: b.name })),
    ],
    [branches],
  );

  const setBranchIdAndReset = (id: string | null) => {
    setBranchId(id || null);
    setPending({});
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [searchProducts, productId]);

  const pendingKeys = useMemo(() => Object.keys(pending), [pending]);
  const activeOverrideCount = useMemo(() => {
    let n = 0;
    for (const v of variants) {
      if (!selectedBranchId) continue;
      const key = `${selectedBranchId}:${v.id}`;
      const p = pending[key];
      if (p === "clear") continue;
      if (p && p.value >= 0) {
        n++;
        continue;
      }
      if (getOverride(v.id, selectedBranchId, overrides)) n++;
    }
    return n;
  }, [variants, selectedBranchId, overrides, pending]);

  const getDisplayOverride = useCallback(
    (v: VariantMatrixRow): string => {
      if (!selectedBranchId) return "";
      const key = `${selectedBranchId}:${v.id}`;
      const p = pending[key];
      if (p === "clear") return "";
      if (p && typeof p === "object") return String(p.value);
      const o = getOverride(v.id, selectedBranchId, overrides);
      return o ? String(o.overridePriceLak) : "";
    },
    [selectedBranchId, overrides, pending],
  );

  const setPendingOverride = useCallback(
    (variantId: string, raw: string) => {
      if (!selectedBranchId) return;
      const key = `${selectedBranchId}:${variantId}`;
      const trimmed = raw.trim();
      if (trimmed === "") {
        setPending((prev) => ({ ...prev, [key]: "clear" }));
        return;
      }
      const num = Number.parseInt(trimmed, 10);
      if (Number.isNaN(num)) return;
      setPending((prev) => ({ ...prev, [key]: { value: num } }));
    },
    [selectedBranchId],
  );

  const setPendingClear = useCallback(
    (variantId: string) => {
      if (!selectedBranchId) return;
      const key = `${selectedBranchId}:${variantId}`;
      setPending((prev) => ({ ...prev, [key]: "clear" }));
    },
    [selectedBranchId],
  );

  const hasOverrideActive = useCallback(
    (v: VariantMatrixRow): boolean => {
      if (!selectedBranchId) return false;
      const key = `${selectedBranchId}:${v.id}`;
      const p = pending[key];
      if (p === "clear") return false;
      if (p && typeof p === "object") return p.value >= 0;
      return !!getOverride(v.id, selectedBranchId, overrides);
    },
    [selectedBranchId, overrides, pending],
  );

  const saveAllPending = async () => {
    if (!selectedBranchId || pendingKeys.length === 0) return;
    setSaving(true);
    try {
      for (const key of pendingKeys) {
        const parts = key.split(":");
        const branchId = parts[0];
        const variantId = parts[1];
        if (!branchId || !variantId) continue;
        const p = pending[key];
        if (p === "clear") {
          await deactivateMutation.mutateAsync({ branchId, variantId });
        } else if (p && typeof p === "object" && p.value >= 0) {
          await upsertMutation.mutateAsync({
            branchId,
            variantId,
            overridePriceLak: p.value,
          });
        }
      }
      setPending({});
      toast.success("ບັນທຶກລາຄາສຳເລັດ");
    } catch {
      toast.error("ບັນທຶກລາຄາລົ້ມເຫຼວ");
    } finally {
      setSaving(false);
    }
  };

  const discardPending = () => {
    setPending({});
    toast.success("ຍົກເລີກການແກ້ໄຂ");
  };

  const isDirty = (v: VariantMatrixRow): boolean => {
    if (!selectedBranchId) return false;
    return `${selectedBranchId}:${v.id}` in pending;
  };

  const getOverrideInputError = (raw: string): string | null => {
    const t = raw.trim();
    if (t === "") return null;
    const num = Number.parseInt(t, 10);
    if (Number.isNaN(num)) return null;
    if (num < 0) return "ລາຄາບໍ່ສາມາດຕ່ຳກວ່າ 0";
    return null;
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຄາຕໍ່ສາຂາ",
              to: "/app/pricing/branch-overrides",
            },
          ]}
        />
      </Header>
      <Main>
        <PricingToolbar
          title="ລາຄາຕໍ່ສາຂາ"
          description="ຕັ້ງລາຄາ override ຕໍ່ສາຂາ ແລະ variant; ລາຄາ base ຈາກ Catalog."
        >
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/pricing/compare" })}
          >
            ປຽບທຽບລາຄາຕໍ່ສາຂາ
          </Button>
        </PricingToolbar>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap font-medium text-muted-foreground text-sm uppercase">
              ສາຂາ:
            </span>
            <SimpleSelect
              value={branchId ?? "none"}
              onValueChange={(v) => setBranchIdAndReset(v || null)}
              options={branchOptions}
              placeholder="ເລືອກສາຂາ..."
              className="min-w-[200px]"
              disabled={branchesQuery.isLoading}
            />
          </div>
          <div className="flex min-w-[200px] flex-1 items-center gap-2">
            <SearchIcon className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ຄົ້ນຫາຕາມຊື່ variant, SKU ຫຼືສິນຄ້າ..."
              defaultValue={searchInputValue}
              onChange={(e) => {
                const v = e.target.value;
                debouncedSetSearch(v);
              }}
              className="max-w-sm"
            />
          </div>
          <div className="flex min-w-[160px] items-center gap-2">
            {/* <span className="whitespace-nowrap text-muted-foreground text-sm">
              ສິນຄ້າ:
            </span> */}
            <InfiniteCombobox<{ id: string; name: string }>
              queryKey={["catalog", "products", "lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/catalog/products/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(
                  `${config.apiUrl}/catalog/products/lookup`,
                  id,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={productId === "all" ? "" : productId}
              onValueChange={(v) => setProductId(v || "all")}
              placeholder="ທຸກສິນຄ້າ"
              className="w-full"
              clearable
            />
          </div>
        </div>

        <div className="rounded-xl border bg-card">
          {!branchId ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
              <p>ເລືອກຢ່າງໜ້ອຍ 1 ສາຂາເພື່ອເບິ່ງຕາຕະລາງລາຄາ</p>
            </div>
          ) : !selectedBranchId ? null : matrixQuery.isLoading ? (
            <div className="py-16 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : variants.length === 0 && !matrixQuery.isLoading ? (
            <div className="py-16 text-center text-muted-foreground">
              ບໍ່ມີ variant ຕາມຕົວກອງ
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[220px]">
                        ຊື່ variant (SKU)
                      </TableHead>
                      <TableHead className="w-[120px] text-right">
                        ລາຄາ base (LAK)
                      </TableHead>
                      <TableHead className="min-w-[160px]">
                        ລາຄາ override (LAK)
                      </TableHead>
                      <TableHead className="w-[140px] text-right">
                        ລາຄາໃຊ້ຈິງ
                      </TableHead>
                      <TableHead className="w-[100px]">ສະຖານະ</TableHead>
                      {canUpdate && (
                        <TableHead className="w-[80px]">ການກະທຳ</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variants.map((v: VariantMatrixRow) => {
                      const override = getOverride(
                        v.id,
                        selectedBranchId,
                        overrides,
                      );
                      const displayVal = getDisplayOverride(v);
                      const pendingVal = pending[
                        `${selectedBranchId}:${v.id}`
                      ] as PendingOverride | undefined;
                      const effectiveNum =
                        pendingVal === "clear"
                          ? v.basePriceLak
                          : pendingVal && typeof pendingVal === "object"
                            ? pendingVal.value
                            : getEffectivePrice(
                                v.id,
                                v.basePriceLak,
                                selectedBranchId,
                                overrides,
                                null,
                              );
                      const error = getOverrideInputError(displayVal);
                      const hasOverride = hasOverrideActive(v);
                      const isBase =
                        !hasOverride ||
                        (effectiveNum === v.basePriceLak && !isDirty(v));

                      return (
                        <TableRow key={v.id}>
                          <TableCell>
                            <div className="font-medium">
                              {v.productName} – {v.variantName}
                            </div>
                            {v.sku && (
                              <div className="text-muted-foreground text-xs">
                                {v.sku}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatLak(v.basePriceLak)}
                          </TableCell>
                          <TableCell>
                            {canUpdate ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    min={0}
                                    placeholder="ບໍ່ມີ override"
                                    value={displayVal}
                                    onChange={(e) =>
                                      setPendingOverride(v.id, e.target.value)
                                    }
                                    className={
                                      "h-9 w-32 text-right" +
                                      (error
                                        ? "border-destructive focus-visible:ring-destructive"
                                        : isDirty(v)
                                          ? "border-primary"
                                          : "")
                                    }
                                  />
                                  {isDirty(v) && (
                                    <span className="text-primary text-xs">
                                      ມີການແກ້
                                    </span>
                                  )}
                                </div>
                                {error && (
                                  <p className="text-destructive text-xs">
                                    {error}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground tabular-nums">
                                {displayVal
                                  ? formatLak(Number(displayVal))
                                  : "ບໍ່ມີ override"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {error ? (
                              <span className="text-destructive">ຜິດພາດ</span>
                            ) : (
                              <>
                                {formatLak(
                                  typeof effectiveNum === "number"
                                    ? effectiveNum
                                    : v.basePriceLak,
                                )}
                                {isBase && (
                                  <span className="text-muted-foreground text-xs">
                                    {" "}
                                    (Base)
                                  </span>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {canUpdate ? (
                              <div className="flex items-center gap-2">
                                <span
                                  className={
                                    hasOverride
                                      ? "text-primary text-xs"
                                      : "text-muted-foreground text-xs"
                                  }
                                >
                                  {hasOverride ? "ເປີດ" : "ປິດ"}
                                </span>
                                <Switch
                                  checked={hasOverride}
                                  onCheckedChange={(checked) => {
                                    if (!checked) setPendingClear(v.id);
                                    else if (override) {
                                      setPendingOverride(
                                        v.id,
                                        String(override.overridePriceLak),
                                      );
                                    } else {
                                      setPendingOverride(
                                        v.id,
                                        String(v.basePriceLak),
                                      );
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                {hasOverride ? "ເປີດ" : "ປິດ"}
                              </span>
                            )}
                          </TableCell>
                          {canUpdate && (
                            <TableCell>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => setPendingClear(v.id)}
                                title="ຍົກເລີກ override"
                              >
                                <RotateCcwIcon className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-muted-foreground text-sm">
                <span>
                  ສະແດງ {fromItem}–{toItem} จาก {totalVariants} ລາຍການ
                  {" · "}
                  <span className="text-primary">
                    {activeOverrideCount} override ເປີດໃຊ້ຢູ່
                  </span>
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </Button>
                  <span className="min-w-[100px] text-center">
                    ໜ້າ {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {pendingKeys.length > 0 && (
          <div className="fixed bottom-6 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 items-center justify-between gap-4 rounded-lg border bg-card px-4 py-3 shadow-lg">
            <span className="font-medium text-sm">
              ມີການແກ້ໄຂທີ່ຍັງບໍ່ບັນທຶກ · {pendingKeys.length} ລາຍການ
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={discardPending}
                disabled={saving}
              >
                ຍົກເລີກ
              </Button>
              <Button size="sm" onClick={saveAllPending} disabled={saving}>
                {saving ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການແກ້ໄຂ"}
              </Button>
            </div>
          </div>
        )}
      </Main>
    </>
  );
}
